import { Globe } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/hooks/useI18n';
import { cn } from '@/lib/utils';

const LANGUAGES = [
  { code: 'zh', label: '中文' },
  { code: 'en', label: 'English' },
  { code: 'ms', label: 'Bahasa Melayu' },
] as const;

interface LanguageSwitcherProps {
  /** Additional CSS classes */
  className?: string;
}

/**
 * Language switch dropdown supporting zh/en/ms.
 *
 * Uses the useI18n hook to read and update the current language.
 * Displays a globe icon with the current language code.
 */
export function LanguageSwitcher({ className }: LanguageSwitcherProps) {
  const { lang, setLang } = useI18n();

  const currentLanguage = LANGUAGES.find((l) => l.code === lang) ?? LANGUAGES[1];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className={cn('gap-1.5', className)}>
          <Globe className="h-4 w-4" />
          <span className="uppercase text-xs font-medium">{currentLanguage.code}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {LANGUAGES.map((language) => (
          <DropdownMenuItem
            key={language.code}
            onClick={() => setLang(language.code)}
            className={cn(
              'cursor-pointer',
              lang === language.code && 'bg-accent font-medium',
            )}
          >
            {language.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
